import { useEffect, useRef, useState } from 'react';
import PropertyService from '../../../services/propertyService';
import messages from './messages';
import type { SwitchStatusProps } from '../../../components/SwitchStatus/types';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import useIntl from '../../../hooks/useIntl';
import type { SelectStatusProps } from '../../../components/SelectStatus/types';

export const useUpdateGPR = (
  currentGPR: string,
  propertyId: string,
  organizationId: string,
  promptToaster: Function,
  intl: Object,
  onHide: Function,
  setPropertyChangeHistoryLog: Function,
) => {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [gpr, setGPR] = useState(currentGPR);

  useEffect(() => {
    setGPR(currentGPR);
  }, [currentGPR]);

  const updateGPR = async (newGPR: string) => {
    const propertyService = new PropertyService();

    try {
      safeSetWrapper(setIsLoading)(true);
      const response = await propertyService.updateGPR(
        organizationId,
        propertyId,
        { gpr: newGPR },
      );

      if (response) {
        safeSetWrapper(setGPR)(response.gpr);
        setPropertyChangeHistoryLog(response.propertyChangeHistoryLog);
      }

      promptToaster({
        title: intl.formatMessage(messages.success),
        message: intl.formatMessage(messages.successBody),
      });

      onHide();
    } catch (_) {
      promptToaster({
        type: 'error',
        title: intl.formatMessage(messages.error),
        message: intl.formatMessage(messages.errorBody),
      });
      safeSetWrapper(setGPR)(gpr);
    } finally {
      safeSetWrapper(setIsLoading)(false);
    }
  };

  const safeSetWrapper = (setFn: Function) => (value: string) => {
    if (isMounted.current) {
      setFn(value);
    }
  };

  return [gpr, safeSetWrapper(setGPR), updateGPR, isLoading];
};

export const useApplicationsSwitchProps = (
  editMode: boolean,
  checked: boolean,
  portalActive: boolean,
  onChange: () => void,
): SwitchStatusProps => {
  const intl = useIntl();
  const hasPermission = usePermissionsHook({
    scope: ['property-update'],
  });
  let tooltip = '';

  if (!portalActive) {
    tooltip = intl.formatMessage(messages.applicationsPortalInactive);
  }

  return {
    enableEdit: editMode,
    showLabelEdit: true,
    label: intl.formatMessage(messages.applicationsOpen),
    switchProps: {
      checked,
      onChange,
    },
    valueLabels: {
      checked: 'Open',
      unchecked: 'Closed',
    },
    disabled: !portalActive || !hasPermission,
    tooltip,
  };
};

export const useAvailabilityVersionSelectProps = (
  editMode: boolean,
  value: string,
  onChange: (value: string) => void,
): SelectStatusProps => {
  const intl = useIntl();

  return {
    label: intl.formatMessage(messages.availabilityDesign),
    enableEdit: editMode,
    options: [
      {
        value: '1',
        text: 'Version 1',
        disabled: false,
      },
      {
        value: '2',
        text: 'Version 2',
        disabled: false,
      },
      {
        value: '2.no-contact',
        text: 'Version 2 - No Contact Form',
        disabled: false,
      },
    ],
    selected: value,
    selectProps: {
      onChange,
    },
  };
};
